import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { locationsAPI, filterUserLocations } from '../../lib/api';
import { MapPin, Phone, ChevronDown, Building, X } from 'lucide-react';

const LocationSelector = () => {
  const { workingLocation, setWorkingLocation, user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const dropdownRef = useRef(null);
  const currentPath = useLocation().pathname;

  useEffect(() => {
    if (user?.id) fetchLocations();
  }, [user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAll({ page_size: 100 });
      const locData = response.data.items || response.data;
      const allLocations = Array.isArray(locData) ? locData : [];
      const filteredLocations = filterUserLocations(allLocations, user);
      setLocations(filteredLocations);
      
      if (filteredLocations.length > 0) {
        const currentLocationValid = workingLocation && filteredLocations.some(l => l.id === workingLocation.id);
        if (!currentLocationValid) {
          setWorkingLocation(filteredLocations[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (location) => {
    setWorkingLocation(location);
    setIsOpen(false);
    setShowInfoPanel(true);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setShowInfoPanel(false);
    }
  };

  const toggleInfoPanel = () => {
    setShowInfoPanel(!showInfoPanel);
    setIsOpen(false);
  };

  // Get contextual label based on current page
  const getContextLabel = () => {
    if (currentPath.includes('/inventory')) return 'Inventory Location';
    if (currentPath.includes('/patients')) return 'Patient Records Location';
    if (currentPath.includes('/visits')) return 'Visits Location';
    if (currentPath.includes('/billing')) return 'Billing Location';
    if (currentPath.includes('/services')) return 'Services Location';
    if (currentPath.includes('/reports')) return 'Reports Location';
    return 'Working Location';
  };

  if (loading) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main Toggle Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors min-w-[200px]"
        data-testid="location-selector"
      >
        <MapPin className="w-4 h-4 text-teal-600" />
        <span className="text-sm font-medium text-slate-700 truncate flex-1 text-left">
          {workingLocation?.name || 'Select location'}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Select Location
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {locations.map((location) => (
              <button
                key={location.id}
                onClick={() => handleLocationSelect(location)}
                className={`w-full px-3 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 ${
                  workingLocation?.id === location.id ? 'bg-teal-50' : ''
                }`}
                data-testid={`location-option-${location.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    workingLocation?.id === location.id ? 'bg-teal-100' : 'bg-slate-100'
                  }`}>
                    <Building className={`w-4 h-4 ${
                      workingLocation?.id === location.id ? 'text-teal-600' : 'text-slate-500'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      workingLocation?.id === location.id ? 'text-teal-700' : 'text-slate-700'
                    }`}>
                      {location.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {location.address}
                    </p>
                  </div>
                  {workingLocation?.id === location.id && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Info Panel (shown after selecting or toggling) */}
      {showInfoPanel && workingLocation && !isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="p-3 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-slate-50 flex items-center justify-between">
            <span className="text-xs font-medium text-teal-700 uppercase tracking-wide">
              {getContextLabel()}
            </span>
            <button
              onClick={() => setShowInfoPanel(false)}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
              data-testid="close-location-info"
            >
              <X className="w-3 h-3 text-slate-500" />
            </button>
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-slate-900">
                  {workingLocation.name}
                </h4>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                  workingLocation.is_active 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {workingLocation.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            
            <div className="mt-4 space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600">{workingLocation.address || 'No address provided'}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="text-slate-600">{workingLocation.phone || 'No phone provided'}</span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">
                All data on this page is filtered for this location.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close info panel overlay */}
      {showInfoPanel && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowInfoPanel(false)}
        />
      )}
    </div>
  );
};

export default LocationSelector;
